import React, { useEffect, useState } from 'react'
import {
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useBreakpointValue,
} from '@chakra-ui/react'

import {
    PrescriptionRefillRequestFieldsFragment,
    PrescriptionRefillRequestType,
} from '~graphql/generated/graphql'
import PrescriptionDenialReasonForm from './prescription-denial-reason-form'
import RefillRequestSummary from './refill-request-summary'

interface Props {
    refillRequest: PrescriptionRefillRequestFieldsFragment
    isOpen: boolean
    onClose: () => void
    onApprove: () => void | Promise<void>
    onDeny: (reason: string) => void | Promise<void>
    isLoading?: boolean
    startOnDenial?: boolean
}

const RefillRequestConfirmModal: React.FC<Props> = ({
    refillRequest,
    isOpen,
    onClose,
    onApprove,
    onDeny,
    isLoading,
    startOnDenial = false,
}) => {
    const modalSize = useBreakpointValue({ base: 'full', md: '3xl' })

    const [isDenying, setIsDenying] = useState<boolean>(startOnDenial)
    const [explanation, setExplanation] = useState<string>('')

    useEffect(() => {
        if (isOpen) {
            setIsDenying(startOnDenial)
        }
    }, [isOpen, startOnDenial])

    useEffect(() => {
        if (!isDenying) {
            setExplanation('')
        }
    }, [isDenying])

    useEffect(() => {
        if (!isOpen) {
            setExplanation('')
        }
    }, [isOpen])

    const isRenewal =
        refillRequest.type === PrescriptionRefillRequestType.Renewal
    const requestTypeLabel = isRenewal ? 'renewal' : 'refill'
    const requestTypeTitle = isRenewal ? 'Renewal' : 'Refill'
    const isExplanationEmpty = !explanation.trim()

    function handleBackOrCancel() {
        if (isDenying) {
            if (startOnDenial) {
                onClose()
            } else {
                setIsDenying(false)
            }
            return
        }
        onClose()
    }

    async function handlePrimaryAction() {
        if (isDenying) {
            if (isExplanationEmpty) return
            await onDeny(explanation.trim())
            return
        }
        await onApprove()
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={modalSize}
            scrollBehavior="inside"
        >
            <ModalOverlay>
                <ModalContent>
                    <ModalHeader borderBottomWidth="1px">
                        <Flex alignItems="center" gap={2}>
                            <Text fontWeight="medium">
                                {isDenying
                                    ? `Deny this prescription ${requestTypeLabel} request?`
                                    : `Approve this prescription ${requestTypeLabel} request?`}
                            </Text>
                            <Badge
                                colorScheme={isRenewal ? 'purple' : 'blue'}
                                variant="subtle"
                                rounded="full"
                                fontSize="2xs"
                                px={2}
                            >
                                {requestTypeTitle}
                            </Badge>
                        </Flex>
                        <Text color="gray.500" fontSize="sm">
                            {isDenying
                                ? `The client will be notified and this ${requestTypeLabel} request will be removed from your action items.`
                                : `This will approve the ${requestTypeLabel} and remove it from your action items.`}
                        </Text>
                    </ModalHeader>
                    <ModalBody px={{ base: 0, md: 6 }} py={{ base: 0, md: 4 }}>
                        <Flex>
                            <Box
                                overflow="hidden"
                                h="full"
                                w="100%"
                                maxW={isDenying ? '0px' : '100%'}
                                opacity={isDenying ? 0 : 1}
                                transition="max-width 0.4s, opacity 0.2s ease-out"
                            >
                                <Box minW="min(100vw, 702px)">
                                    <Box
                                        flex={1}
                                        px={{ base: 4, md: 0 }}
                                        py={{ base: 4, md: 0 }}
                                    >
                                        <RefillRequestSummary
                                            refillRequest={refillRequest}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                            <Box
                                overflow="hidden"
                                h="full"
                                w="100%"
                                maxW={isDenying ? '100%' : '0px'}
                                opacity={isDenying ? 1 : 0}
                                transition="max-width 0.4s, opacity 0.2s ease-out"
                            >
                                <Box minW="min(100vw, 702px)">
                                    <Box
                                        flex={1}
                                        px={{ base: 4, md: 0 }}
                                        py={{ base: 4, md: 0 }}
                                    >
                                        <RefillRequestSummary
                                            refillRequest={refillRequest}
                                            hideStatus
                                        />
                                    </Box>
                                    <Box w="full" mt={6}>
                                        <PrescriptionDenialReasonForm
                                            refillRequest={refillRequest}
                                            explanation={explanation}
                                            setExplanation={setExplanation}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Flex>
                    </ModalBody>
                    <ModalFooter
                        justifyContent="space-between"
                        borderTopWidth="1px"
                    >
                        <Button onClick={handleBackOrCancel}>
                            {isDenying && !startOnDenial ? 'Back' : 'Cancel'}
                        </Button>
                        <HStack spacing={4}>
                            {!isDenying && (
                                <Button
                                    variant="link"
                                    onClick={() => setIsDenying(true)}
                                    size="sm"
                                    textDecor="underline"
                                    color="gray.700"
                                >
                                    Deny {requestTypeTitle}
                                </Button>
                            )}
                            <Button
                                colorScheme={isDenying ? 'red' : 'brand'}
                                isLoading={isLoading}
                                onClick={handlePrimaryAction}
                                isDisabled={isDenying && isExplanationEmpty}
                            >
                                {isDenying
                                    ? `Deny ${requestTypeTitle}`
                                    : `Approve ${requestTypeTitle}`}
                            </Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </ModalOverlay>
        </Modal>
    )
}

export default RefillRequestConfirmModal
